import React, { useCallback, useEffect, useMemo, useState } from "react";
import FormField from "../form-field";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";

const defaultForm = {
  department: "",
  departmentIds: [],
  role: "",
  employeeId: "",
  employmentType: "",
  dateJoined: "",
  level: "",
  branch: "",
  position: "",
  pensionProvider: "",
  pensionNumber: "",
  hmo: "",
  hmoId: "",
  hmoNumber: "",
  hmoStatus: "",
  hmoPlan: "",
  hmoHospital: "",
};

const toSelectOptions = (items, placeholder) => [
  { value: "", label: placeholder },
  ...(items || []).map((item) => ({ value: item.id, label: item.name })),
];

const Organisation = ({ data, onDataChange, errors = {}, showErrors = false, generatedEmployeeId = "" }) => {
  const apiBaseUrl = getApiBaseUrl();
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState("");
  const [orgOptions, setOrgOptions] = useState({
    departments: [],
    roles: [],
    levels: [],
    positions: [],
    branches: [],
  });

  const employmentTypes = [
    { value: "", label: "Select employment type" },
    { value: "full-time", label: "Full-time" },
    { value: "contract", label: "Contract" },
    { value: "intern", label: "Intern" },
  ];

  const hmoOptions = [
    { value: "", label: "Select HMO status" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const onChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  }, []);

  const [form, setForm] = useState({ ...defaultForm, ...(data || {}) });

  useEffect(() => {
    onDataChange?.(form);
  }, [form, onDataChange]);

  const departmentOptions = useMemo(
    () => toSelectOptions(orgOptions.departments, optionsLoading ? "Loading departments..." : "Select department"),
    [orgOptions.departments, optionsLoading],
  );

  const filteredRoles = useMemo(
    () => (orgOptions.roles || []).filter((role) => !form.department || role.departmentId === form.department),
    [form.department, orgOptions.roles],
  );

  const roleOptions = useMemo(
    () => toSelectOptions(filteredRoles, form.department ? "Select role" : "Choose department first"),
    [filteredRoles, form.department],
  );

  const levelOptions = useMemo(
    () => toSelectOptions(orgOptions.levels, optionsLoading ? "Loading levels..." : "Select level"),
    [orgOptions.levels, optionsLoading],
  );

  const positionOptions = useMemo(
    () => toSelectOptions(orgOptions.positions, optionsLoading ? "Loading positions..." : "Select position"),
    [orgOptions.positions, optionsLoading],
  );

  const branchOptions = useMemo(
    () => toSelectOptions(orgOptions.branches, optionsLoading ? "Loading branches..." : "Select branch"),
    [orgOptions.branches, optionsLoading],
  );

  useEffect(() => {
    const loadOnboardingOptions = async () => {
      if (!apiBaseUrl) {
        setOptionsError("API base URL is not configured.");
        return;
      }

      try {
        setOptionsLoading(true);
        setOptionsError("");

        const response = await axios.get(`${apiBaseUrl}/organizations/onboarding/options`);
        const options = response?.data?.options || {};

        setOrgOptions({
          departments: options.departments || [],
          roles: options.roles || [],
          levels: options.levels || [],
          positions: options.positions || [],
          branches: options.branches || [],
        });
      } catch (requestError) {
        const message = requestError?.response?.data?.message || "Unable to load onboarding options.";
        setOptionsError(Array.isArray(message) ? message.join(", ") : message);
      } finally {
        setOptionsLoading(false);
      }
    };

    loadOnboardingOptions();
  }, [apiBaseUrl]);

  useEffect(() => {
    if (!form.role) {
      return;
    }

    const selectedRole = (orgOptions.roles || []).find((role) => role.id === form.role);
    if (selectedRole && selectedRole.departmentId !== form.department) {
      setForm((prev) => ({ ...prev, role: "" }));
    }
  }, [form.department, form.role, orgOptions.roles]);

  useEffect(() => {
    if (form.department && !(form.departmentIds || []).includes(form.department)) {
      setForm((prev) => ({ ...prev, departmentIds: [...(prev.departmentIds || []), prev.department] }));
    }
  }, [form.department, form.departmentIds]);

  const toggleDepartment = (departmentId) => {
    if (departmentId === form.department) return;
    setForm((prev) => ({
      ...prev,
      departmentIds: (prev.departmentIds || []).includes(departmentId)
        ? prev.departmentIds.filter((id) => id !== departmentId)
        : [...(prev.departmentIds || []), departmentId],
    }));
  };

  return (
    <div className="font-Outfit text-base  mx-auto text-black   bg-white ">
      <form className="">
        {optionsError ? (
          <p className="mb-4 text-sm text-red-600" role="alert">
            {optionsError}
          </p>
        ) : null}

        <div className="grid lg:w-[75%] grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="department"
            name="department"
            label="Primary Department"
            required
            as="select"
            options={departmentOptions}
            value={form.department}
            onChange={onChange}
            error={showErrors ? errors.department : ""}
          />

          <fieldset className="md:col-span-2 rounded-xl border border-gray-200 p-4">
            <legend className="px-1 text-sm font-semibold text-[#071C50]">All department assignments</legend>
            <p className="mb-3 text-xs text-gray-500">The primary department controls the employee's role and main reporting line.</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(orgOptions.departments || []).map((department) => {
                const isPrimary = department.id === form.department;
                const checked = isPrimary || (form.departmentIds || []).includes(department.id);
                return (
                  <label key={department.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <input type="checkbox" checked={checked} disabled={isPrimary} onChange={() => toggleDepartment(department.id)} />
                    <span className="min-w-0 flex-1 truncate">{department.name}</span>
                    {isPrimary ? <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-[#1234CD]">Primary</span> : null}
                  </label>
                );
              })}
            </div>
          </fieldset>

          <FormField
            id="employeeId"
            name="employeeId"
            label="Employee ID"
            value={generatedEmployeeId || ""}
            onChange={() => {}}
            placeholder="Auto-generated after onboarding"
            readOnly
            className="bg-gray-50"
          />

          <FormField
            id="role"
            name="role"
            as="select"
            label="Role"
            required
            options={roleOptions}
            value={form.role}
            onChange={onChange}
            disabled={!form.department}
            error={showErrors ? errors.role : ""}
          />

          <FormField
            id="employmentType"
            name="employmentType"
            as="select"
            label="Employment Type"
            required
            options={employmentTypes}
            value={form.employmentType}
            onChange={onChange}
            error={showErrors ? errors.employmentType : ""}
          />

          <FormField
            id="dateJoined"
            name="dateJoined"
            label="Date Joined"
            required
            type="date"
            value={form.dateJoined}
            onChange={onChange}
            error={showErrors ? errors.dateJoined : ""}
          />

          <FormField
            id="level"
            name="level"
            as="select"
            label="Level"
            required
            options={levelOptions}
            value={form.level}
            onChange={onChange}
            error={showErrors ? errors.level : ""}
          />

          <FormField
            id="position"
            name="position"
            as="select"
            label="Position (Optional)"
            options={positionOptions}
            value={form.position}
            onChange={onChange}
            error={showErrors ? errors.position : ""}
          />

          <FormField
            id="branch"
            name="branch"
            as="select"
            label="Branch"
            required
            options={branchOptions}
            value={form.branch}
            onChange={onChange}
            error={showErrors ? errors.branch : ""}
          />

          <FormField
            id="pensionProvider"
            name="pensionProvider"
            label="Pension Provider"
            placeholder="Pension provider"
            value={form.pensionProvider}
            onChange={onChange}
          />

          <FormField
            id="pensionNumber"
            name="pensionNumber"
            label="Pension Number"
            placeholder="Pension Number"
            value={form.pensionNumber}
            onChange={onChange}
          />
        </div>

        <div className="grid lg:w-full my-4 grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            id="hmo"
            name="hmo"
            label="On HMO?"
            as="select"
            options={hmoOptions}
            value={form.hmo}
            onChange={onChange}
          />

          <FormField
            id="hmoId"
            name="hmoId"
            label="HMO ID"
            value={form.hmoId}
            onChange={onChange}
            placeholder="HMO ID"
          />

          <FormField
            id="hmoNumber"
            name="hmoNumber"
            label="HMO Number"
            placeholder="HMO number"
            value={form.hmoNumber}
            onChange={onChange}
          />

          <FormField
            id="hmoStatus"
            name="hmoStatus"
            label="HMO Status"
            placeholder="Status"
            value={form.hmoStatus}
            onChange={onChange}
          />

          <FormField
            id="hmoPlan"
            name="hmoPlan"
            label="HMO Plan"
            placeholder="HMO Plan"
            value={form.hmoPlan}
            onChange={onChange}
          />

          <FormField
            id="hmoHospital"
            name="hmoHospital"
            label="HMO Hospital"
            placeholder="Hospital"
            value={form.hmoHospital}
            onChange={onChange}
          />
        </div>
      </form>
    </div>
  );
};

export default Organisation;
